/**
 * WizardBottomBar
 *
 * Fixed bottom action bar used on steps 1–6.
 * Shows: back button (left), helper/status text (centre), skip + next (right).
 * Welcome (0) and Completion (7) screens use their own self-contained CTA areas.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { OnboardingStep } from '@/contexts/OnboardingContext';

const PRIMARY = '#00796B';

interface WizardBottomBarProps {
  currentStep: OnboardingStep;
  /** Text or node shown in the centre of the bar */
  helperText?: React.ReactNode;
  /** Whether the next button should be disabled */
  nextDisabled?: boolean;
  /** Whether the skip button should be hidden */
  hideSkip?: boolean;
  /** Whether the back button should be hidden */
  hideBack?: boolean;
  /** Label override for the next button */
  nextLabel?: string;
  onBack?: () => void;
  onSkip?: () => void;
  onNext?: () => void;
}

export function WizardBottomBar({
  currentStep,
  helperText,
  nextDisabled = false,
  hideSkip = false,
  hideBack = false,
  nextLabel,
  onBack,
  onSkip,
  onNext,
}: WizardBottomBarProps) {
  const isLastStep = currentStep === 7;
  const resolvedNextLabel = nextLabel ?? (isLastStep ? 'Finish Setup →' : 'Next Step →');

  return (
    <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-3">
      <div className="flex items-center gap-4">
        {/* Back button */}
        <div className="w-24">
          {!hideBack && (
            <Button variant="ghost" size="sm" className="gap-1 text-gray-600" onClick={onBack}>
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
          )}
        </div>

        {/* Centre helper text */}
        <div className="flex-1 text-center">
          {helperText && (
            <p className="text-xs text-muted-foreground leading-relaxed">{helperText}</p>
          )}
        </div>

        {/* Right: Skip + Next */}
        <div className="flex items-center gap-2 w-auto shrink-0">
          {!hideSkip && (
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-gray-700"
              onClick={onSkip}
            >
              Skip for now
            </Button>
          )}

          <Button
            size="sm"
            disabled={nextDisabled}
            style={!nextDisabled ? { backgroundColor: PRIMARY, color: 'white' } : undefined}
            className={nextDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}
            onClick={onNext}
          >
            {resolvedNextLabel}
            {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
